import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type { ClinicInterface } from 'src/app/shared/models/clinic.interface';
import type { UserInterface } from 'src/app/shared/models/user.interface';
import type { UserDetailsInterface, UserDtoInterface, UserShortInterface } from '../models/user-dto.interface';
import { UserRoleEnum } from '../models/user-dto.interface';

export const GetUsersByClinicIdQuery =  gql`query getUsersByClinicId($clinicId: Long!, $isMyPacs: Boolean) {
  usersByClinicId(clinicId: $clinicId, isMyPacs: $isMyPacs) {
    users {
      id
      email
      firstName
      lastName
      roles
    }
    clinic {
      id
      name
      shortName
      isMyPacs
    }
  }
}`;

export const GetUserByEmailQuery =  gql`query getUsersByEmail($email: String!) {
  userByEmail(email: $email) {
    email
    firstName
    lastName
    roles
    signature
    orderNum
    clinics {
      id
      name
    }
  }
}`;

export const GetUserByOrderNumQuery =  gql`query getUserByOrderNum($orderNum: String!) {
  userByOrderNum(orderNum: $orderNum) {
    firstName
    lastName
  }
}`;

export const GetAllUsersQuery = gql`query getAllUsers {
  allUsers {
    id
    email
    firstName
    lastName
    roles
    clinics {
      id
      name
      shortName
      isMyPacs
    }
  }
}`;

export let GetUsersByClinicIdQueryVariables: { clinicId?: number, isMyPacs?: boolean } = {};

@Injectable()
export class AdminUserService {
  constructor(private readonly apollo: Apollo) {}

  getUsersByClinicId(clinicId: number, isMyPacs?: boolean): Observable<ClinicAndUsersDto[]> {
    GetUsersByClinicIdQueryVariables = { clinicId, isMyPacs };
    return this.apollo.watchQuery<GetUsersByClinicIdReponse>({
      query : GetUsersByClinicIdQuery,
      variables : GetUsersByClinicIdQueryVariables
    }).valueChanges.pipe(map(res => res.data.usersByClinicId));
  }

  getUserByEmail(email: string): Observable<UserDetailsInterface> {
    return this.apollo.query<GetUserByEmailReponse>({
      query : GetUserByEmailQuery,
      variables : { email }
    }).pipe(map(res => res.data.userByEmail));
  }

  getUserByOrderNum(orderNum: string): Observable<UserShortInterface> {
    return this.apollo.query<GetUserByOrderNumReponse>({
      query : GetUserByOrderNumQuery,
      variables : { orderNum }
    }).pipe(map(res => res.data.userByOrderNum));
  }

  getAllUsers(): Observable<UserDetailsInterface[]> {
    return this.apollo.watchQuery<GetAllUsersResponse>({
      query: GetAllUsersQuery
    }).valueChanges.pipe(map(res => res.data.allUsers));
  }

  addUser(user: UserDtoInterface, origin: 'MY_PACS' | 'SCC'): Observable<UserInterface | undefined> {
    return this.apollo.mutate<{ registerUserByVet: UserInterface }>({
      mutation : gql`mutation addUser($dto: UserRegisterDtoInput, $origin: EOrigin!) {
        registerUserByVet(dto: $dto, origin: $origin) {
          id
          email
          firstName
          lastName
          roles
          signature
          orderNum
        }
      }`,
      variables: {
        dto: user,
        origin
      },
      refetchQueries: [{
        query : GetUsersByClinicIdQuery,
        variables : GetUsersByClinicIdQueryVariables
      }]
    }).pipe(map(res =>res.data ? res.data?.registerUserByVet : undefined));
  }

  updateUser(user: UserDtoInterface): Observable<UserInterface | undefined> {
    return this.apollo.mutate<{ updateUser: UserInterface }>({
      mutation : gql`mutation updateUser($dto: UserRegisterDtoInput) {
        updateUser(dto: $dto) {
          id
          email
          firstName
          lastName
          roles
          signature
          orderNum
        }
      }`,
      variables: { dto: user },
      refetchQueries: [{
        query : GetUsersByClinicIdQuery,
        variables : GetUsersByClinicIdQueryVariables
      },
      {
        query : GetUserByEmailQuery,
        variables : { email : user.email }
      }]
    }).pipe(map(res =>res.data ? res.data?.updateUser : undefined));
  }

  deleteUser(email: string): Observable<UserInterface | undefined> {
    return this.apollo.mutate<{ deleteUser: UserInterface }>({
      mutation : gql`mutation deleteUser($email: String!) {
        deleteUser(email: $email)
      }`,
      variables: { email },
      refetchQueries: [{
        query : GetUsersByClinicIdQuery,
        variables : GetUsersByClinicIdQueryVariables
      }]
    }).pipe(map(res =>res.data ? res.data.deleteUser : undefined));
  }

  convertToRole(role: string): UserRoleEnum | undefined {
    switch (role.toUpperCase()) {
      case 'ADMIN':
        return UserRoleEnum.ADMIN;
      case 'CLIENT':
        return UserRoleEnum.CLIENT;
      case 'VETERINARY':
        return UserRoleEnum.VETERINARY;
      case 'SECRETARY':
        return UserRoleEnum.SECRETARY;
      case 'INVITEDVET':
        return UserRoleEnum.INVITED_VET;
      case 'CLINICADMIN':
        return UserRoleEnum.CLINIC_ADMIN;
      case 'RADIOLOGIST':
        return UserRoleEnum.RADIOLOGIST;
      case 'SCCVETERINARY':
        return UserRoleEnum.SCC_VETERINARY;
      case 'SCCREVIEWER':
        return UserRoleEnum.SCC_REVIEWER;
      case 'ADMINSCC':
        return UserRoleEnum.ADMIN_SCC;
      default:
        return;
    }
  }

  connectAs(userId: number): Observable<string> {
    return this.apollo.mutate<{ loginAsUser: string }>({
      mutation : gql`mutation loginAsUser($userId: Long!) {
        loginAsUser(userId: $userId)
      }`,
      variables: {
        userId
      }
    }).pipe(map(res => res.data?.loginAsUser ?? ''));
  }
}

export interface GetUsersByClinicIdReponse {
  usersByClinicId: ClinicAndUsersDto[];
}

export interface GetUserByEmailReponse {
  userByEmail: UserDetailsInterface;
}

export interface ClinicAndUsersDto {
  clinic: ClinicInterface;
  users: UserInterface[];
}

export interface GetUserByOrderNumReponse {
  userByOrderNum: UserShortInterface;
}

export interface GetAllUsersResponse {
  allUsers: UserDetailsInterface[];
}
